import { DateTime } from 'luxon';

import APIDate from './APIDate';

/**
 * This type is based off of PostgreSQL's `tstzrange` type.
 *
 * An example `["2013-10-01 07:00:00-07","2013-10-01 07:15:00-07")`
 * The `[` means inclusive, so this range starts at 07:00:00
 * The `)` means exclusive, so the range is up to 07:14:59 essentially (not including millis)
 *
 * @param begin - The begin timestamp in epoch millis
 * @param end - The end timestamp in epoch millis or undefined if this is open-ended
 * @param isLowerBoundInclusive - If the lower bound is inclusive (i.e. `[`), otherwise it is exclusive `(`
 * @param isUpperBoundInclusive - If the upper bound is inclusive (i.e. `]`), otherwise it is exclusive `)`
 * @readonly beginsAt - The Date object this period begins at
 * @readonly endsAt - I don't want to have to live with a type of `Date | undefined` -- that's a pain. .If it is open ended, we'll return the highest Date we can define.
 * @readonly isExpired -
 */
class TimePeriod {
  begin: APIDate = new APIDate();
  end: APIDate = new APIDate();
  isLowerBoundInclusive: boolean;
  isUpperBoundInclusive: boolean;

  get beginsAt(): Date {
    // if lower-bound exclusive, add one second so comparisons will be accurate
    const d = (this.begin.dateTime as DateTime).toJSDate();
    if (!this.isLowerBoundInclusive) {
      d.setSeconds(d.getSeconds() + 1);
    }
    return d;
  }

  get endsAt(): Date {
    if (this.end.dateTime) {
      const d = this.end.dateTime.toJSDate();
      if (!this.isUpperBoundInclusive) {
        d.setSeconds(d.getSeconds() - 1);
      }
      return d;
    }
    // this is the highest date JS supports +033658-09-27T01:46:39.999Z
    return new Date(999999999999999);
  }

  get isExpired(): boolean {
    return Date.now() > this.endsAt.getTime();
  }

  constructor(part?: Partial<TimePeriod>) {
    if (part) Object.assign(this, part);
  }

  static fromServer(data: Partial<ServerTimePeriod> = {}): TimePeriod {
    const o = new TimePeriod();
    Object.assign(o, data, {
      begin: new APIDate(data.begin || DateTime.fromJSDate(new Date()).toMillis()),
      end: new APIDate(data.end),
    });
    return o;
  }

  toServer(): ServerTimePeriod {
    return {
      ...this,
      begin: this.begin.millis,
      end: this.end.millis,
    };
  }
}

// Note: Can't use ServerModelTransformer here b/c ServerModelTransformer imports TimePeriod type
interface ServerTimePeriod extends Omit<TimePeriod, 'begin' | 'end'> {
  begin: number | undefined;
  end: number | undefined;
}

export default TimePeriod;
export type { ServerTimePeriod };
